/**
 * grass_field.ts — the camera-following FIELD the grass shader reads (2026-09-25, visual-realism R2).
 *
 * One RGBA32F texture, 256 × 256 texels at 0.5 m (a 128 m window), TOROIDALLY addressed: absolute
 * texel (X, Z) lives in slot (X mod 256, Z mod 256), so the window scrolls by rewriting only the
 * tiles that enter it — never a copy. Per texel:
 *   x = the RENDERED ground height at the texel centre (meshHeight — the chunk's own triangle
 *       interpolation, so a blade's foot lands on the mesh the player sees; bilinear between texel
 *       centres 0.5 m apart departs from a 5.3 m triangle mesh only across a crease, by at most
 *       Δslope × 0.125 m),
 *   y = blade density 0..1 (−1 = not yet filled: the shader collapses any blade that touches it),
 *   z = mean blade height (m),
 *   w = the turf colour, sRGB bytes packed r·65536 + g·256 + b (exact in a float).
 * The rules (grass_rules.ts) run on a 2 m lattice per 8 m tile and interpolate to the texels; the
 * height is exact per texel. Tiles fill NEAREST FIRST under a per-frame time budget, and only where
 * the rendered chunk exists (`covered`) — the analytic fallback would cost 4 noise stacks a texel
 * and disagree with the mesh anyway.
 *
 * Pure (no three.js): the grass layer uploads `data`; the harness drives it with synthetic terrain.
 */
import { grassRule, type GrassSample, type GrassOut } from './grass_rules.js';
import { linearToSrgbByte } from './turf_core.js';

export const GRASS_FIELD_N = 256;
export const GRASS_TEXEL_M = 0.5;
export const GRASS_TILE_TEXELS = 16;
export const GRASS_TILE_M = GRASS_TILE_TEXELS * GRASS_TEXEL_M; // 8 m
export const GRASS_TILES = GRASS_FIELD_N / GRASS_TILE_TEXELS;   // 16 per axis
export const GRASS_COARSE_M = 2;
/** The window reaches at least this far from the camera in every direction (half size − the 4 m re-centre slack). */
export const GRASS_FIELD_REACH_M = (GRASS_TILES / 2) * GRASS_TILE_M - GRASS_TILE_M / 2;

export interface GrassFieldSource {
  /** True when the rendered terrain covers the rect (meshHeight is the mesh there). */
  covered(x0: number, z0: number, x1: number, z1: number): boolean;
  /** The rendered ground height (meshHeight). */
  height(x: number, z: number): number;
  /** Everything the rules need but the height and slope (biome weights, canopy, road, blocked, water, noises). */
  sample(x: number, z: number, h: number, slope: number, out: GrassSample): void;
  /**
   * THE GROUND KEEP-OUT, per TEXEL (2026-10-02, lib/ground_keepout.ts): a predicate for one tile — true where a
   * building or an interior stands on that ground — or null when nothing reaches the tile. Applied at the 0.5 m
   * texels, never the 2 m lattice: interpolated, a lattice block left a 1–2 m fringe of grass inside every wall.
   */
  tileKeepOut?(x0: number, z0: number, x1: number, z1: number): ((x: number, z: number, h: number) => boolean) | null;
}

const mod = (a: number, n: number): number => ((a % n) + n) % n;
const C = GRASS_TILE_M / GRASS_COARSE_M + 1; // 5 coarse points per tile side

export class GrassField {
  readonly data = new Float32Array(GRASS_FIELD_N * GRASS_FIELD_N * 4);
  /** Absolute tile each slot holds (x, z) and whether it is filled. */
  private readonly tileX = new Int32Array(GRASS_TILES * GRASS_TILES).fill(-0x7fffffff);
  private readonly tileZ = new Int32Array(GRASS_TILES * GRASS_TILES).fill(-0x7fffffff);
  private readonly filled = new Uint8Array(GRASS_TILES * GRASS_TILES);
  /** The window's centre tile. */
  centreTX = Number.NaN;
  centreTZ = Number.NaN;
  /** Bumped on every texel write (the layer uploads when it moves). */
  version = 0;
  readonly stats = { tilesFilled: 0, tilesPending: 0, lastFillMs: 0, invalidations: 0 };
  private readonly smp: GrassSample = { w: new Float32Array(6), forest: 0, road: 0, blocked: false, h: 0, slope: 0, sea: 0, water: null, patch: 0, dryN: 0, ground: null };
  private readonly out: GrassOut = { density: 0, bladeH: 0, rgb: [0, 0, 0], dry: 0, wet: 0 };
  private readonly cD = new Float32Array(C * C);
  private readonly cH = new Float32Array(C * C);
  private readonly cR = new Float32Array(C * C);
  private readonly cG = new Float32Array(C * C);
  private readonly cB = new Float32Array(C * C);

  constructor() { this.clear(); }

  /** Forget everything (world change): every slot unfilled. */
  clear(): void {
    for (let i = 0; i < this.data.length; i += 4) { this.data[i] = 0; this.data[i + 1] = -1; this.data[i + 2] = 0; this.data[i + 3] = 0; }
    this.tileX.fill(-0x7fffffff); this.tileZ.fill(-0x7fffffff); this.filled.fill(0);
    this.centreTX = Number.NaN; this.centreTZ = Number.NaN;
    this.version++;
  }

  /** Mark a slot's tile unfilled (density −1 over its texels). */
  private unfill(slot: number): void {
    if (!this.filled[slot] && this.tileX[slot] !== -0x7fffffff) { /* already unfilled */ }
    this.filled[slot] = 0;
    const sx = (slot % GRASS_TILES) * GRASS_TILE_TEXELS, sz = Math.floor(slot / GRASS_TILES) * GRASS_TILE_TEXELS;
    for (let j = 0; j < GRASS_TILE_TEXELS; j++) {
      let o = ((sz + j) * GRASS_FIELD_N + sx) * 4 + 1;
      for (let i = 0; i < GRASS_TILE_TEXELS; i++, o += 4) this.data[o] = -1;
    }
    this.version++;
  }

  /** Invalidate every tile of the window overlapping an absolute rect (a chunk rebuilt under it). */
  invalidate(x0: number, z0: number, x1: number, z1: number): void {
    const tx0 = Math.floor(x0 / GRASS_TILE_M), tx1 = Math.floor((x1 - 1e-6) / GRASS_TILE_M);
    const tz0 = Math.floor(z0 / GRASS_TILE_M), tz1 = Math.floor((z1 - 1e-6) / GRASS_TILE_M);
    for (let tz = tz0; tz <= tz1; tz++) for (let tx = tx0; tx <= tx1; tx++) {
      const slot = mod(tz, GRASS_TILES) * GRASS_TILES + mod(tx, GRASS_TILES);
      if (this.tileX[slot] === tx && this.tileZ[slot] === tz && this.filled[slot]) { this.unfill(slot); this.stats.invalidations++; }
    }
  }

  /**
   * Follow the camera (absolute metres) and fill pending tiles nearest-first for up to `budgetMs`.
   * Returns the number of tiles filled this call.
   */
  update(camX: number, camZ: number, budgetMs: number, src: GrassFieldSource, now: () => number = () => performance.now()): number {
    const ctx = Math.round(camX / GRASS_TILE_M), ctz = Math.round(camZ / GRASS_TILE_M);
    const half = GRASS_TILES / 2;
    if (ctx !== this.centreTX || ctz !== this.centreTZ) {
      this.centreTX = ctx; this.centreTZ = ctz;
      for (let tz = ctz - half; tz < ctz + half; tz++) for (let tx = ctx - half; tx < ctx + half; tx++) {
        const slot = mod(tz, GRASS_TILES) * GRASS_TILES + mod(tx, GRASS_TILES);
        if (this.tileX[slot] !== tx || this.tileZ[slot] !== tz) { this.tileX[slot] = tx; this.tileZ[slot] = tz; this.unfill(slot); }
      }
    }
    // pending tiles, nearest first
    const pend: Array<{ slot: number; d: number }> = [];
    for (let slot = 0; slot < this.filled.length; slot++) {
      if (this.filled[slot]) continue;
      const cx = (this.tileX[slot] + 0.5) * GRASS_TILE_M, cz = (this.tileZ[slot] + 0.5) * GRASS_TILE_M;
      pend.push({ slot, d: (cx - camX) * (cx - camX) + (cz - camZ) * (cz - camZ) });
    }
    pend.sort((a, b) => a.d - b.d);
    const t0 = now();
    let n = 0;
    for (const p of pend) {
      if (n > 0 && now() - t0 > budgetMs) break;
      if (this.fillTile(p.slot, src)) n++;
    }
    this.stats.tilesFilled += n;
    this.stats.tilesPending = pend.length - n;
    if (n) this.stats.lastFillMs = now() - t0;
    return n;
  }

  private fillTile(slot: number, src: GrassFieldSource): boolean {
    const tx = this.tileX[slot], tz = this.tileZ[slot];
    const x0 = tx * GRASS_TILE_M, z0 = tz * GRASS_TILE_M;
    if (!src.covered(x0, z0, x0 + GRASS_TILE_M, z0 + GRASS_TILE_M)) return false;
    // the rules on the 2 m lattice (5 × 5, shared edges re-evaluated — cheaper than a neighbour cache)
    for (let j = 0; j < C; j++) for (let i = 0; i < C; i++) {
      const x = x0 + i * GRASS_COARSE_M, z = z0 + j * GRASS_COARSE_M;
      const h = src.height(x, z);
      const gx = (src.height(x + 1, z) - src.height(x - 1, z)) / 2, gz = (src.height(x, z + 1) - src.height(x, z - 1)) / 2;
      const slope = Math.hypot(gx, gz);
      const s = this.smp;
      s.h = h; s.slope = slope; s.water = null; s.blocked = false; s.ground = null;
      src.sample(x, z, h, slope, s);
      const o = grassRule(s, this.out);
      const k = j * C + i;
      this.cD[k] = o.density; this.cH[k] = o.bladeH; this.cR[k] = o.rgb[0]; this.cG[k] = o.rgb[1]; this.cB[k] = o.rgb[2];
    }
    const ko = src.tileKeepOut ? src.tileKeepOut(x0, z0, x0 + GRASS_TILE_M, z0 + GRASS_TILE_M) : null;
    const sx = (slot % GRASS_TILES) * GRASS_TILE_TEXELS, sz = Math.floor(slot / GRASS_TILES) * GRASS_TILE_TEXELS;
    const lerp2 = (a: Float32Array, i0: number, j0: number, fx: number, fz: number): number => {
      const k = j0 * C + i0;
      return (a[k] * (1 - fx) + a[k + 1] * fx) * (1 - fz) + (a[k + C] * (1 - fx) + a[k + C + 1] * fx) * fz;
    };
    for (let j = 0; j < GRASS_TILE_TEXELS; j++) {
      const lz = (j + 0.5) * GRASS_TEXEL_M; // metres into the tile
      const cz = lz / GRASS_COARSE_M; const j0 = Math.min(C - 2, Math.floor(cz)); const fz = cz - j0;
      let o = ((sz + j) * GRASS_FIELD_N + sx) * 4;
      for (let i = 0; i < GRASS_TILE_TEXELS; i++, o += 4) {
        const lx = (i + 0.5) * GRASS_TEXEL_M;
        const cx = lx / GRASS_COARSE_M; const i0 = Math.min(C - 2, Math.floor(cx)); const fx = cx - i0;
        this.data[o] = src.height(x0 + lx, z0 + lz);
        this.data[o + 1] = ko && ko(x0 + lx, z0 + lz, this.data[o]) ? 0 : lerp2(this.cD, i0, j0, fx, fz);
        this.data[o + 2] = lerp2(this.cH, i0, j0, fx, fz);
        const r = linearToSrgbByte(lerp2(this.cR, i0, j0, fx, fz)), g = linearToSrgbByte(lerp2(this.cG, i0, j0, fx, fz)), b = linearToSrgbByte(lerp2(this.cB, i0, j0, fx, fz));
        this.data[o + 3] = r * 65536 + g * 256 + b;
      }
    }
    this.filled[slot] = 1;
    this.version++;
    return true;
  }

  /** The shader's slot offset for the current render anchor: (2·anchor) mod 256 per axis (the anchor is whole metres). */
  static slotOffset(anchorX: number, anchorZ: number): [number, number] {
    const k = 1 / GRASS_TEXEL_M;
    return [mod(Math.round(anchorX * k), GRASS_FIELD_N), mod(Math.round(anchorZ * k), GRASS_FIELD_N)];
  }

  /** The texel record at an ABSOLUTE texel index (slot lookup). */
  texel(X: number, Z: number): [number, number, number, number] {
    const o = (mod(Z, GRASS_FIELD_N) * GRASS_FIELD_N + mod(X, GRASS_FIELD_N)) * 4;
    return [this.data[o], this.data[o + 1], this.data[o + 2], this.data[o + 3]];
  }

  /**
   * CPU mirror of the shader's fetch at ABSOLUTE (x, z): bilinear between texel centres; null when
   * any of the four taps is unfilled (the shader collapses such a blade).
   */
  sampleAt(x: number, z: number): { h: number; density: number; bladeH: number } | null {
    const fx = x / GRASS_TEXEL_M - 0.5, fz = z / GRASS_TEXEL_M - 0.5;
    const X0 = Math.floor(fx), Z0 = Math.floor(fz), ax = fx - X0, az = fz - Z0;
    const a = this.texel(X0, Z0), b = this.texel(X0 + 1, Z0), c = this.texel(X0, Z0 + 1), d = this.texel(X0 + 1, Z0 + 1);
    if (a[1] < 0 || b[1] < 0 || c[1] < 0 || d[1] < 0) return null;
    const bl = (i: number): number => (a[i] * (1 - ax) + b[i] * ax) * (1 - az) + (c[i] * (1 - ax) + d[i] * ax) * az;
    return { h: bl(0), density: bl(1), bladeH: bl(2) };
  }

  /** Is the absolute tile holding (x, z) filled? */
  filledAt(x: number, z: number): boolean {
    const tx = Math.floor(x / GRASS_TILE_M), tz = Math.floor(z / GRASS_TILE_M);
    const slot = mod(tz, GRASS_TILES) * GRASS_TILES + mod(tx, GRASS_TILES);
    return this.tileX[slot] === tx && this.tileZ[slot] === tz && this.filled[slot] === 1;
  }
}
